/**
 * Gemini API client for image generation
 * Calls the generate-render Edge Function
 */

import { supabase } from './supabase-client';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { RenderMode } from '../types/property';
import { convertUrlToWebPBase64 } from './image-utils';

export interface GenerateRenderParams {
    imageUrl: string;
    mode: RenderMode | 'transform';
    style?: string;
    roomName: string;
    userId: string;
    propertyId?: string;
    imageId?: string;
    // New transform flow fields
    selectedActions?: any[];
    analysisId?: string | null;
}

export interface GeneratedImage {
    gcsPath: string;
    signedUrl: string;
    base64?: string;
    variantIndex?: number;
    provider?: string;           // 'gemini' | 'nanobanana2' | 'gemini_fallback' | 'nanobanana2_fallback'
    promptUsed?: string;         // Prompt used for generation
    generationTimeMs?: number;   // Processing time in milliseconds
}

export interface GenerateRenderResponse {
    success: boolean;
    generationId?: string;
    status?: 'processing';     // async: always 'processing' on success
    images?: GeneratedImage[]; // deprecated: variants now arrive via Realtime
    error?: string;
    errorCode?: string;
}

/**
 * Generate AI-enhanced images using Gemini API
 * @param params - Generation parameters including source image URL and mode
 * @returns Generated images with signed URLs
 */
export async function generateRender(params: GenerateRenderParams): Promise<GenerateRenderResponse> {
    try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
            throw new Error('User not authenticated');
        }

        const supabaseUrl = `https://${projectId}.supabase.co`;
        const functionUrl = `${supabaseUrl}/functions/v1/generate-render`;

        console.log('Calling Gemini Edge Function:', functionUrl);
        const startTime = Date.now();

        // Convert + resize image on frontend to avoid Edge Function memory limit
        const resized = await convertUrlToWebPBase64(params.imageUrl);

        const requestBody: Record<string, any> = {
            roomName: params.roomName,
            userId: params.userId,
            propertyId: params.propertyId,
            imageId: params.imageId,
        };

        // Action-based flow (new) vs mode-based flow (legacy)
        if (params.selectedActions) {
            requestBody.selectedActions = params.selectedActions;
            requestBody.analysisId = params.analysisId;
        } else {
            requestBody.mode = params.mode;
            requestBody.style = params.style;
        }

        if (resized) {
            requestBody.imageBase64 = resized.base64;
            requestBody.imageMimeType = resized.mimeType;
            requestBody.imageWidth = resized.width;
            requestBody.imageHeight = resized.height;
        } else {
            // Fallback: send URL if resize failed (e.g. CORS issue)
            requestBody.imageUrl = params.imageUrl;
        }

        // Use Anon Key for Authorization to avoid session token issues (401)
        const response = await fetch(
            functionUrl,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                    'apikey': publicAnonKey,
                },
                body: JSON.stringify(requestBody),
            }
        );

        if (!response.ok) {
            // Try to parse JSON error response for errorCode
            try {
                const errorJson = await response.json();
                console.error('Edge Function error:', response.status, errorJson);
                return {
                    success: false,
                    error: errorJson.error || `Edge Function error: ${response.status}`,
                    errorCode: errorJson.errorCode,
                };
            } catch {
                const errorText = await response.text();
                console.error('Edge Function error:', response.status, errorText);
                return {
                    success: false,
                    error: `Edge Function error: ${response.status} - ${errorText}`,
                };
            }
        }

        const result: GenerateRenderResponse = await response.json();
        console.log('Gemini Edge Function response (Time: ' + (Date.now() - startTime) + 'ms):', result);

        /* Return result directly as logical check matches server response */
        return result;
    } catch (error) {
        console.error('Generate render error:', error);
        return {
            success: false,
            error: String(error),
        };
    }
}

/**
 * Map style/persona values to API-friendly format
 */
export function getStyleForMode(mode: RenderMode, persona?: string, style?: string): string | undefined {
    if (mode === 'staging') {
        // Map persona to style
        const personaToStyle: Record<string, string> = {
            'student': 'minimalist',
            'family': 'contemporary',
            'premium': 'luxury',
        };
        return personaToStyle[persona || 'family'] || 'contemporary';
    }

    if (mode === 'renovation') {
        return style || 'modern';
    }

    return undefined;
}
